import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import BrandSelect from 'features/playground/components/BrandSelect';
import SliderRow from 'shared/components/ui/SliderRow';
import { menuPanelClass, drawerIconButtonClass, textButtonClass } from 'features/playground/styles';

/**
 * ModelConfigPopup: a draggable "Model parameters" popover opened from a task's
 * Params button. Shows the generation knobs for the chosen model (some are
 * OpenAI-family only). All values live on the panel's `model` config; unset =
 * not sent to the provider.
 *
 * props: { model, provider, onChange(patch), onClose, anchor?: {x,y} }
 */

const OPENAI_FAMILY = new Set(['openai', 'azure_openai', 'openai_compat', 'groq', 'deepseek', 'fireworks', 'mistral', 'xai']);

const labelCls = 'text-[12px] font-medium text-[color:var(--text-primary)]';

export default function ModelConfigPopup({ model = {}, provider, onChange, onClose, anchor }) {
  const [pos, setPos] = useState(anchor && anchor.x != null ? anchor : null);
  const cardRef = useRef(null);
  const isOpenAI = OPENAI_FAMILY.has(provider);

  useEffect(() => {
    const onKey = (e) => {
      // An open dropdown inside the popover takes its own Escape first.
      if (e.key === 'Escape' && !document.querySelector('[role="listbox"]')) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const startDrag = (e) => {
    if (e.target.closest('button')) return;
    e.preventDefault();
    const rect = cardRef.current.getBoundingClientRect();
    const dx = e.clientX - rect.left;
    const dy = e.clientY - rect.top;
    const move = (ev) => setPos({ x: ev.clientX - dx, y: ev.clientY - dy });
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const patch = (p) => onChange(p);
  const stops = useMemo(() => (Array.isArray(model.stop) ? model.stop : []), [model.stop]);

  const positionStyle = pos
    ? { left: pos.x, top: pos.y }
    : { left: '50%', top: 96, transform: 'translateX(-50%)' };

  return createPortal(
    <div
      ref={cardRef}
      role="dialog"
      aria-label="Model parameters"
      className={`fixed z-[60] w-[300px] !py-0 ${menuPanelClass}`}
      style={{ ...positionStyle, fontFamily: 'Inter, sans-serif' }}
    >
      {/* draggable header */}
      <div
        onPointerDown={startDrag}
        className="flex h-10 cursor-grab items-center gap-2 border-b border-[color:var(--border-default)] pl-4 pr-2 active:cursor-grabbing"
      >
        <h2 className="flex-1 text-[13px] font-semibold text-[color:var(--text-primary)]">Model parameters</h2>
        <button type="button" onClick={onClose} aria-label="Close" title="Close" className={drawerIconButtonClass}>
          <X size={14} />
        </button>
      </div>

      <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto p-4">
        <SliderRow label="Temperature" value={model.temperature} min={0} max={2} step={0.1}
          onChange={(v) => patch({ temperature: v })} onClear={() => patch({ temperature: '' })} />
        <SliderRow label="Max tokens" value={model.max_tokens} min={0} max={16384} step={128}
          onChange={(v) => patch({ max_tokens: v })} onClear={() => patch({ max_tokens: '' })} />
        <SliderRow label="Top p" value={model.top_p} min={0} max={1} step={0.05}
          onChange={(v) => patch({ top_p: v })} onClear={() => patch({ top_p: '' })} />
        {isOpenAI && (
          <>
            <SliderRow label="Frequency penalty" value={model.frequency_penalty} min={-2} max={2} step={0.1}
              onChange={(v) => patch({ frequency_penalty: v })} onClear={() => patch({ frequency_penalty: '' })} />
            <SliderRow label="Presence penalty" value={model.presence_penalty} min={-2} max={2} step={0.1}
              onChange={(v) => patch({ presence_penalty: v })} onClear={() => patch({ presence_penalty: '' })} />
          </>
        )}

        {/* stop sequences */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className={labelCls}>Stop sequences</span>
            <button type="button" onClick={() => patch({ stop: [...stops, ''] })} className={textButtonClass}>
              Add
            </button>
          </div>
          {stops.map((s, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="flex items-center gap-1.5">
              <input
                value={s}
                aria-label={`Stop sequence ${i + 1}`}
                onChange={(e) => patch({ stop: stops.map((x, j) => (j === i ? e.target.value : x)) })}
                placeholder="e.g. \n\n"
                className="h-8 w-full rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 font-mono text-[12px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] focus:border-[color:var(--border-strong)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]"
              />
              <button type="button" onClick={() => patch({ stop: stops.filter((_, j) => j !== i) })}
                aria-label="Remove stop sequence" title="Remove" className={drawerIconButtonClass}>
                <X size={13} />
              </button>
            </div>
          ))}
        </div>

        {/* tool choice */}
        <div className="flex items-center justify-between gap-3">
          <span className={labelCls}>Tool choice</span>
          <BrandSelect
            value={model.tool_choice || 'none'}
            onChange={(v) => patch({ tool_choice: v })}
            ariaLabel="Tool choice"
            minWidth={120}
            options={[
              { value: 'none', label: 'None' },
              { value: 'auto', label: 'Auto' },
              { value: 'required', label: 'Required' },
            ]}
          />
        </div>

        {/* use cache */}
        <label className="flex cursor-pointer items-center justify-between">
          <span className={labelCls}>Use cache</span>
          <input type="checkbox" checked={Boolean(model.use_cache)}
            onChange={(e) => patch({ use_cache: e.target.checked })}
            style={{ accentColor: 'var(--text-primary)' }} className="h-4 w-4" />
        </label>
      </div>
    </div>,
    document.body,
  );
}
